import classNames from 'classnames';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { Badge, Button, MenuItem, Nav, Navbar, NavDropdown, NavItem, OverlayTrigger, Popover, ProgressBar } from 'react-bootstrap';
import { connect } from 'react-redux';
import { matchPath, withRouter } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import { branch, compose, defaultProps, lifecycle, renderComponent, renderNothing, setPropTypes, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { navLinks, routes, tabKeys } from '../../../constants';
import logo from '../../../images/logo_rev.png';
import bookmarkOutlined from '../../../images/bookmark-outlined.svg';
import { fullName, isBusinessAdmin, isExternalUser, isManager, isPublisher, isSystemOrSuperAdmin, isTeamManager } from '../../../services';
import { businessActions, businessSelector, publicNonCpdContentSelector, fullTeamPendingExternalCpdSelector, specificTeamPendingExternalCpdSelector } from '../../../state/modules/business';
import { currentAttemptSelector } from '../../../state/modules/quiz';
import { sessionActions } from '../../../state/modules/session';
import { taskCountSelector } from '../../../state/modules/task/selectors';
import { currentUserIndustry, currentUserSelector, userActions } from '../../../state/modules/user';
import { noCapMap } from '../../../utils';
import { ConfirmationModal, Icon } from '../../components';
import SupportForm from './support-form';
import { dashboardEventSelector } from '../../../state/modules/search-tile';

const navDropdownTitle = (
  <button className='btn navbar-btn'>
    <Icon icon='settings' />
  </button>
);

const Progress = branch(
  ({ progress }) => !progress,
  renderNothing
)(({ progress }) => (
  <ProgressBar className='flex-header' {...progress} />
));

const ClosedNav = ({
  closeBtnText,
  routeAfterClose,
  taskId
}) => (
    <ul className='nav navbar-nav navbar-right navbar-signup'>
      <li>
        <LinkContainer exact isActive={fp.stubFalse} to={taskId ? routes.app.tasks.view(taskId) : routeAfterClose}>
          <Button bsStyle='plain'>
            {taskId ? 'Return to Task' : closeBtnText} <Icon icon='close' />
          </Button>
        </LinkContainer>
      </li>
    </ul>
  );

const MenuNav = branch(
  ({ showCloseNav }) => showCloseNav,
  renderComponent(ClosedNav)
)(({
  isBusinessAdmin,
  isManager,
  isTeamManager,
  isPublisher,
  isSystemAdmin,
  isExternalUser,
  logout,
  userFullName,
  mockUserId,
  logoutMockUser,
  showSupportForm,
  toggleSupportForm,
  setSupportForm,
  submitSupportForm,
  userGoals,
  defaultCpdLabel,
  showContentDropdown,
  setShowContentDropDown,
  showBusinessContent,
  currentUserBookmarks,
  ...props
}) => (
    <Nav pullRight>
      {
        mockUserId ?
          <NavDropdown
            id='mock-user-nav-menu'
            title={`Logged in as ${userFullName}`}
            noCaret={true}>
            <NavItem>
              <Button className='full' onClick={logoutMockUser} bsStyle='plain'>Back to Admin</Button>
            </NavItem>
          </NavDropdown> : null
      }
      {
        <NavDropdown
          id='content-nav-dropdown'
          open={showContentDropdown}
          onToggle={() => { }}
          onMouseEnter={() => setShowContentDropDown(true)}
          onMouseLeave={() => setShowContentDropDown(false)}
          title={`Content`}>
          <LinkContainer to={routes.app.training.home} exact={true}>
            <NavItem eventKey='default'>
              {defaultCpdLabel}
            </NavItem>
          </LinkContainer>
          {
            fp.map(
              ({ goalId, name }) => {
                return <LinkContainer key={`goal-${goalId}`} to={routes.app.training.goal(goalId)} exact={true}>
                  <NavItem eventKey={goalId}>
                    {name}
                  </NavItem>
                </LinkContainer>
              }
            )(userGoals)
          }
          {
            showBusinessContent ?
              <LinkContainer to={routes.app.training.businessContentHome} exact={true}>
                <NavItem eventKey='non-cpd-event'>
                  Business Library
                  </NavItem>
              </LinkContainer> :
              null
          }
        </NavDropdown>
      }
      {
        fp.flow(
          fp.filter((link) =>
            fp.getOr(fp.stubTrue)('shouldDisplay')(link)()
          ),
          noCapMap(({ title, route, exact, notification }, index) =>
            <LinkContainer className={`nav-link-${title}`} key={title} to={route} exact={exact}>
              <NavItem>
                {title}
                {
                  notification ? <Badge bsClass='task-notification'>{notification}</Badge> : null
                }
              </NavItem>
            </LinkContainer>
          )
        )(navLinks(props))
      }
      {
        currentUserBookmarks.length > 0 
        ? <LinkContainer to={routes.app.training.bookmark} exact={true}>
            <NavItem eventKey='bookmark'>
              <OverlayTrigger trigger={['hover']} rootClose placement='bottom'
                overlay={
                  <Popover id='tooltip' className='tooltip fit'>
                    <p className='no-padding no-margin'>My bookmarked content</p>
                  </Popover>
                }>
                <span className='flexbox align-center nowrap'>
                  <img
                    src={bookmarkOutlined}
                    alt='Bookmark'
                    width={28}
                    style={{ filter: 'invert(100%)' }}
                  />
                </span>
              </OverlayTrigger>
            </NavItem>
          </LinkContainer> 
        : null
      }
      <LinkContainer to={routes.app.externalCpd.details} exact={true}>
        <NavItem eventKey='default'>
          <OverlayTrigger trigger={['hover']} rootClose placement='bottom'
            overlay={
              <Popover id='tooltip' className='tooltip fit'>
                <p className='no-padding no-margin'>Upload external CPD activity</p>
              </Popover>
            }>
            <span className='flexbox align-center nowrap'>
              <Icon icon='upload' />
            </span>
          </OverlayTrigger>
        </NavItem>
      </LinkContainer>
      <NavDropdown
        id='nav-menu'
        title={navDropdownTitle}
        noCaret={true}>
        <MenuItem header>
          Logged in as {userFullName}
        </MenuItem>
        <MenuItem divider />
        {
          isPublisher ?
            <LinkContainer to={routes.publisherPortal.dashboard}>
              <MenuItem>
                Go to Publisher Portal
              </MenuItem>
            </LinkContainer> :
            null
        }
        {
          isSystemAdmin ?
            <LinkContainer to={routes.adminPortal.dashboard}>
              <MenuItem>
                Go to Admin Portal
              </MenuItem>
            </LinkContainer> :
            null
        }
        {
          <LinkContainer to={routes.app.professionalProfile.root}>
            <MenuItem>
              Professional Profile
            </MenuItem>
          </LinkContainer>
        }
        {
          isExternalUser ?
            null :
            <LinkContainer to={routes.app.accountSettings()}>
              <MenuItem>
                Account settings
              </MenuItem>
            </LinkContainer>
        }
        {
          (isBusinessAdmin || isTeamManager) ?
            <LinkContainer to={routes.app.usersAndTeamsSettings(isBusinessAdmin ? tabKeys.usersAndTeamsSettings.userManagement : tabKeys.usersAndTeamsSettings.teams)}>
              <MenuItem>
                {`Users & Teams`}
              </MenuItem>
            </LinkContainer> :
            null
        }
        <LinkContainer to={isManager ? routes.app.trainingPlansSettings(tabKeys.trainingPlansSettings.businessTrainingPlan) : routes.app.trainingPlansSettings()}>
          <MenuItem>
            Training Plans
          </MenuItem>
        </LinkContainer>
        {
          isExternalUser ?
            null :
            <MenuItem onClick={toggleSupportForm}>
              Support
            </MenuItem>
        }
        {
          isExternalUser ?
            null :
            <ConfirmationModal
              className="support-request-modal"
              show={showSupportForm}
              actions={[
                {
                  onConfirmAction: () => {
                    submitSupportForm();
                    toggleSupportForm();
                  },
                  actionLabel: 'Submit',
                }
              ]}
              cancel={() => toggleSupportForm()}
              message={<SupportForm handleUpdate={setSupportForm} />}
              headingText={<span>Click <Link target='_blank' to='https://caddie-public-document.s3-ap-southeast-2.amazonaws.com/Caddie+Handbook+Aug2021.pdf'>here</Link> to view our user help guide or you can send us a support request below</span>}
            />
        }
        {
          isExternalUser ?
            null :
            <MenuItem
              onClick={(e) => logout()}>
              Log out
            </MenuItem>
        }
      </NavDropdown>
    </Nav>
  ));

const AppNav = ({
  progress,
  ...props
}) => (
    <Navbar
      role='navigation'
      fluid={true}
      className={classNames('navbar-signup', { 'container-flex': progress, mockOtherUser: props.mockUserId })}>
      <Navbar.Header className={classNames({ 'flex-header': progress })}>
        <Navbar.Toggle />
        <Navbar.Brand>
          <Link to={props.isManager ? routes.app.teams.root : routes.app.dashboard}>
            <img src={logo} alt='Caddie' />
          </Link>
        </Navbar.Brand>
      </Navbar.Header>
      <Progress {...{ progress }} />
      <Navbar.Collapse className={classNames({ 'flex-header': progress })}>
        <MenuNav {...props} />
      </Navbar.Collapse>
    </Navbar>
  );

const mapStateToProps = (state) => ({
  currentAttempt: currentAttemptSelector(state),
  user: currentUserSelector(state),
  business: businessSelector(state),
  totalQuizQuestions: fp.getOr(null)('selectedContent.quizSize')(state.content),
  taskCount: taskCountSelector(state),
  mockUserId: state.session.mockUserId,
  userGoals: fp.flow(
    fp.get('trainingPlans'),
    fp.flatMap('goals')
  )(state.user.profile),
  industry: currentUserIndustry(state),
  nonCpdContent: publicNonCpdContentSelector(state),
  dashboardEvent: dashboardEventSelector(state),
  currentUserBookmarks: state.user.currentUserBookmarks,
  teamPendingExternalCpd: fullTeamPendingExternalCpdSelector(state),
  specificTeamPendingExternalCpd: specificTeamPendingExternalCpdSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  logout: bindActionCreators(sessionActions.session.logout, dispatch),
  logoutMockUser: bindActionCreators(sessionActions.session.logoutMockUser, dispatch),
  sendSupportRequest: bindActionCreators(userActions.user.sendSupportRequest, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
  getNonCpdContent: bindActionCreators(businessActions.business.content.getNonCpdContent, dispatch),
  getFullTeamPendingExternalCpd: bindActionCreators(businessActions.business.getFullTeamPendingExternalCpd, dispatch),
  getSpecificTeamPendingExternalCpd: bindActionCreators(businessActions.business.getSpecificTeamPendingExternalCpd, dispatch),
});

export default compose(
  setPropTypes({
    showCloseNav: PropTypes.bool,
    routeAfterClose: PropTypes.string,
    closeBtnText: PropTypes.string
  }),
  defaultProps({
    showCloseNav: false,
    routeAfterClose: routes.app.dashboard,
    closeBtnText: ''
  }),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        user,
        business,
        getNonCpdContent,
        getUserBookmarks,
        getFullTeamPendingExternalCpd,
        getSpecificTeamPendingExternalCpd,
      } = this.props

      if (business) {
        getNonCpdContent(business.id);
        
        // If the user is business admin, get all pending external CPDs.
        // If the user is team manager, get the pending external CPDs for his team.
        if (isBusinessAdmin(user)){
          getFullTeamPendingExternalCpd(business.id);
        } else if (isTeamManager(user, business)){
          const teamId =  fp.flow(
            fp.filter(role => role.isTeamManager),
            fp.first,
            fp.get("businessTeamId")
          )(user.teamMemberRoles);
          getSpecificTeamPendingExternalCpd(business.id, teamId);
        }
      }
      getUserBookmarks();
    }
  }),
  withProps(({
    currentAttempt,
    user,
    business,
    industry
  }) => ({
    defaultCpdLabel: fp.getOr('CPD')('[0].defaultProgressLabel')(industry),
    currentQuizAttemptQuestion: currentAttempt ?
      fp.getOr([])('questionAttempts')(currentAttempt).length + 1 :
      null,
    isBusinessAdmin: isBusinessAdmin(user),
    isManager: isManager(user),
    isPublisher: fp.overSome([isPublisher, isSystemOrSuperAdmin])(user),
    isTeamManager: isTeamManager(user, business),
    isSystemAdmin: isSystemOrSuperAdmin(user),
    isExternalUser: isExternalUser(user)
  })),
  withProps(
    ({ nonCpdContent, isBusinessAdmin, business }) => ({
      showBusinessContent: business && (!fp.isEmpty(nonCpdContent) || isBusinessAdmin)
    })
  ),
  withProps(({
    currentQuizAttemptQuestion,
    location: {
      pathname,
      search
    },
    totalQuizQuestions,
    user
  }) => ({
    userFullName: fullName(user),
    progress: matchPath(pathname, { path: routes.app.training.content }) &&
      currentQuizAttemptQuestion !== null && totalQuizQuestions > 0 ?
      {
        now: ((currentQuizAttemptQuestion) / totalQuizQuestions) * 100,
        label: (
          <span className='progress-level'>
            {`Question ${currentQuizAttemptQuestion} of ${totalQuizQuestions}`}
          </span>
        )
      } :
      null,
    taskId: queryString.parse(search).taskId
  })),
  withProps(
    ({ user, isBusinessAdmin, isTeamManager, teamPendingExternalCpd, specificTeamPendingExternalCpd }) => ({
      pendingExternalCpdCount: isBusinessAdmin 
        ? teamPendingExternalCpd ? teamPendingExternalCpd.length : null 
        : isTeamManager
            // Team manager cannot approve self CPDs.
            ? specificTeamPendingExternalCpd ? specificTeamPendingExternalCpd.filter(({ userId }) => userId !== user.id).length : null 
            : null,
    }),
  ),
  withStateHandlers(
    {
      showContentDropdown: false,
      showSupportForm: false,
      supportForm: {}
    },
    {
      setShowContentDropDown: () => (showContentDropdown) => ({ showContentDropdown }),
      toggleSupportForm: ({ showSupportForm }) => () => {
        return {
          showSupportForm: !showSupportForm
        }
      },
      setSupportForm: () => (supportForm) => ({ supportForm })
    }
  ),
  withHandlers({
    submitSupportForm: ({ supportForm, user, sendSupportRequest }) => () => {
      const username = fullName(user)
      const { email } = user
      const data = {
        ...supportForm,
        email,
        username
      }
      sendSupportRequest(data)
    }
  })
)(AppNav);



// WEBPACK FOOTER //
// ./src/containers/partials/nav/app-nav.js